import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, inject, signal, viewChild,
} from '@angular/core'
import { canTakeFocus, focusedElement, refocus } from '../../../utils/focus'
import { ModalShellComponent } from '../../modal-shell/modal-shell.component'
import { BasemapApiKeyDialogService } from '../services/basemap-api-key-dialog.service'
import { MAPTILER_KEY_PROVIDER } from '../services/basemap-api-key.service'

/**
 * Collects the MapTiler API key. Hosted once in AppComponent (driven by
 * BasemapApiKeyDialogService), so it sits above every map's stacking context
 * and survives the layer control being collapsed or destroyed.
 *
 * The key is only ever the input's value: it is never logged, tracked, or put
 * in notice/aria text, and a stored key is never prefilled.
 */
@Component({
  selector: 'app-basemap-api-key-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalShellComponent],
  template: `
    <app-modal-shell title="MapTiler API key" size="sm" activityName="basemap_key"
                     [closeOnBackdrop]="true" (closed)="svc.cancel()">
      <form id="basemap-key-form" (submit)="onSubmit(); $event.preventDefault()">
        @if (isChange()) {
          <p class="key-note">A key is already saved on this browser; saving replaces it.</p>
        }
        <div class="field">
          <label class="field-label" for="basemap-key-input">API key</label>
          <div class="key-input-row">
            <input #keyInput id="basemap-key-input" class="field-input" data-modal-initial-focus
                   [type]="revealed() ? 'text' : 'password'"
                   autocomplete="off" spellcheck="false" autocapitalize="off"
                   aria-describedby="basemap-key-help basemap-key-error"
                   [attr.aria-invalid]="error() ? 'true' : null"
                   [value]="value()"
                   (input)="onInput($any($event.target).value)" />
            <button type="button" class="btn btn-secondary btn-sm"
                    [attr.aria-pressed]="revealed()"
                    (click)="revealed.set(!revealed())">{{ revealed() ? 'Hide' : 'Show' }}</button>
          </div>
          <p id="basemap-key-error" class="key-error" role="alert">{{ error() }}</p>
        </div>
        <p id="basemap-key-help" class="key-help">
          The two MapTiler basemaps (MapTiler Satellite, MapLibre Satellite) share one
          {{ provider.name }} key. Get a free key at
          <a [href]="provider.keysUrl" target="_blank" rel="noopener noreferrer">cloud.maptiler.com</a>
          → Account → API keys. It is stored only in this browser.
        </p>
      </form>
      <div actions>
        <button type="button" class="btn btn-secondary" (click)="svc.cancel()">Cancel</button>
        <button type="submit" form="basemap-key-form" class="btn btn-primary">Save</button>
      </div>
    </app-modal-shell>
  `,
  styles: [`
    .key-input-row { display: flex; gap: 8px; align-items: center; }
    .key-input-row .field-input { flex: 1; min-width: 0; }
    .key-note, .key-help { font-size: 0.8rem; color: var(--color-text-secondary); margin: 0 0 12px; }
    .key-error { font-size: 0.8rem; color: var(--color-danger); margin: 6px 0 0; }
    .key-error:empty { display: none; }
  `],
})
export class BasemapApiKeyDialogComponent {
  protected readonly svc = inject(BasemapApiKeyDialogService)
  private readonly keyInput = viewChild.required<ElementRef<HTMLInputElement>>('keyInput')

  protected readonly provider = MAPTILER_KEY_PROVIDER
  protected readonly isChange = computed(() => this.svc.request()?.mode === 'change')
  protected readonly value = signal('')
  protected readonly revealed = signal(false)
  protected readonly error = signal('')

  constructor() {
    const opener = focusedElement()

    // The input is marked for the shell's initial focus, which lands on it only while this dialog is topmost.

    // The shell traps Tab and returns focus to the opener too, but only after
    // the render and only to the opener; this return runs first and adds the
    // layer panel's fallback.
    inject(DestroyRef).onDestroy(() => this.restoreFocus(opener))
  }

  protected onInput(v: string): void {
    this.value.set(v)
    if (this.error()) this.error.set('')
  }

  protected onSubmit(): void {
    const trimmed = this.value().trim()
    if (!trimmed) {
      this.error.set('Enter an API key.')
      this.keyInput().nativeElement.focus()
      return
    }
    if (!this.svc.submit(trimmed)) this.error.set("Couldn't save the key in this browser.")
  }

  /** Back to the opener, or — when it is gone or hidden — the layer panel's toggle; nothing when it was `body`. */
  private restoreFocus(opener: HTMLElement | null): void {
    if (!opener) return
    const target = canTakeFocus(opener) ? opener : document.querySelector<HTMLElement>('[data-basemap-key-return]')
    refocus(target)
  }
}
